import { DAY, SHORT_MONTH_WITH_DAY } from '../../scripts/constants/date-time-formats';
import { _unique } from '../../scripts/utilities/array-utils';
import _pairs from 'lodash/fp/toPairs';
import CARRIERS from '../../scripts/constants/operating-carriers';

import BookedLegUIModel from './leg';

class LegOriginCollectionUIModel {
  constructor(legs = []) {
    this._legCollection = [];

    for (let i = 0; i < legs.length; i++) {
      const leg = new BookedLegUIModel(legs[i]);
      this._legCollection.push(leg);
    }
  }

  /**
   * @return {Array} array of BookedLegUIModel
   */
  getAllLegs() {
    return this._legCollection;
  }

  /**
   * Returns the selected segments information as LegUIModel instances, ordered by leg number.
   *
   * @return LegUIModel[]
   */
  getSelectedSegmentsAsLegUIModels(isMulticity = false) {
    const BookedLegUIModelArray = [];
    const segmentPairs = _pairs(this.segments);

    if (isMulticity) {
      const legFare = this.legFares['1'];
      segmentPairs.forEach(entry => {
        // No mover el legNumb
        const segmentCollectionModel = entry['1'];
        BookedLegUIModelArray.push(BookedLegUIModel.create(segmentCollectionModel, legFare));
      });
    } else {
      segmentPairs.forEach(entry => {
        const [legNumber, segmentCollectionModel] = entry;
        const legFare = this.legFares[legNumber];
        BookedLegUIModelArray.push(BookedLegUIModel.create(segmentCollectionModel, legFare));
      });
    }

    return BookedLegUIModelArray;
  }

  /**
   * @return {Moment}
   */
  getDepartureDate() {
    return this._legCollection[0].departureDateTime;
  }

  getArrivalDate() {
    return this._legCollection[0].arrivalDateTime;
  }

  /**
   *
   * @return {Array} departure airport, arrival airport for itinerary, e.g. ['MEX', 'JFK']
   */
  getItineraryCityPair() {
    return this._legCollection[0].getLegCityPair();
  }

  /**
   * @param  {String} storefront [description]
   * @return {String} e.g. Jun 20—30, Jun 20-Jul 6, Jun 20
   */
  getItineraryDateString(storefront) {
    const departureDate = this.getDepartureDate();
    const returnDate = this.getReturnDate();

    if (returnDate && !departureDate.isSame(returnDate, 'day')) {
      const isReturnMonthSameAsDeparture = departureDate.isSame(returnDate, 'month');
      const departureString = departureDate.format(SHORT_MONTH_WITH_DAY[storefront]);
      const returnFormat = isReturnMonthSameAsDeparture ? DAY : SHORT_MONTH_WITH_DAY[storefront];
      const returnString = returnDate.format(returnFormat);

      // round trip, e.g. Jun 20—30, Jun 20-Jul 6
      return `${departureString}–${returnString}`;
    } else {
      // one way, or returns same day, e.g. Jun 20
      return departureDate.format(SHORT_MONTH_WITH_DAY[storefront]);
    }
  }

  /**
   * @param  {String} legCode - alphanumeric code for specific leg
   * @return {BookedLegUIModel} - matching leg
   */
  getLegByCode(legCode) {
    const legs = this.getAllLegs();

    const matchingCartIndex = legs.findIndex(leg => leg.legCode === legCode);

    return legs[matchingCartIndex];
  }

  getNumberOfLegs() {
    return this.getAllLegs().length;
  }


  getOperatingCarriers() {
    const legs = this.getAllLegs();
    const carrierInfo = [];
    legs.forEach((legInfo) => {
      legInfo.segmentsCollection.forEach((segment) => {
        carrierInfo.push(segment.operatingCarrier);
      });
    });
    return _unique(carrierInfo);
  }

  getOnlyOperatedBy(carrier) {
    return this.getOperatingCarriers().filter(
      c => c !== carrier
    ).length === 0;
  }

  getOperatingCarrierLabelData() {
    const operatingCarriers = this.getOperatingCarriers();

    let operatingCarrierCopyID = null;
    let operatingCarrier = null;
    let operatingCarrierLabel = null;

    if (operatingCarriers.length === 1) {
      operatingCarrier = operatingCarriers[0];
      operatingCarrierCopyID = 'APP_OPERATED_BY';
      operatingCarrierLabel = CARRIERS[operatingCarrier];
    } else {
      operatingCarrierCopyID = 'OPERATED_BY_MULTIPLE';
    }

    return {
      operatingCarrier,
      operatingCarrierLabel,
      operatingCarrierCopyID,
    };
  }

  /**
   * @return {Object} number of segments per leg, e.g. { 1: 2, 2: 3 }
   * (leg 1 has 2 segments, leg 2 has 3 segments)
   * Used on booking flow seatmaps to determine current leg number
   */
  getNumberOfSegmentsPerLeg() {
    const segmentsPerLeg = {};

    this._legCollection.forEach((bookedLegModel, i) => {
      const legNumber = i + 1;

      segmentsPerLeg[legNumber] = bookedLegModel.segments.length;
    });

    return segmentsPerLeg;
  }

  /**
   * @return {Moment}
   */
  getReturnDate() {
    if (!this._legCollection[1]) {
      return false;
    } else {
      return this._legCollection[1].departureDateTime;
    }
  }

  getSegmentByCode(segmentCode) {
    let segment = null;
    this._legCollection.forEach(legModel => {
      if (legModel.getSegmentByCode(segmentCode) !== undefined) {
        segment = legModel.getSegmentByCode(segmentCode);
      }
    });
    return segment;
  }

  /**
   * @return {Array} segment codes for all legs
   */
  getSegmentCodesForAllLegs() {
    let segmentCodes = [];

    this._legCollection.forEach(bookedLegModel => {
      if (bookedLegModel.manageStatus === 'OPEN') {
        segmentCodes = segmentCodes.concat(bookedLegModel.getSegmentCodes());
      }
    });
    return segmentCodes;
  }
}

export default LegOriginCollectionUIModel;
